import { App, Form } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import { AnimatePresence } from "motion/react"
import { useEffect, useMemo, useRef, useState } from "react"

import { classSessionAtoms, projectAtoms } from "#atoms"
import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import TextOverrun from "#blocks/TextOverrun"
import { NULL_ID } from "#classes/Firebase"
import { UNKNOWN_PROJECT_COLOR } from "#constants/dropdownOptions"
import { type Hexcode, useThemeContext } from "#contexts/ThemeContextProvider"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { type ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { type Uuid } from "#customTypes/globalTypes"
import ClassSessionDetails from "#forms/AddClass/ClassSessionDetails"
import { useIsFulfilled } from "#hooks/jotai/useCascadingData"
import { useSomeDocs, useStableIds } from "#hooks/jotai/useSubscription"
import {
  generateSessionDates,
  generateSessionId,
  isDefined,
} from "#utils/classHelpers"
import { getProjectScheduleBySession } from "#utils/curriculumHelpers"
import { getNow, isTodayOrFuture } from "#utils/dateHelpers"

export interface SessionInfo {
  id: Uuid
  startDate: string
  sessionIndex: number
  isOverflow: boolean
  project?: ProjectConstructor
  fraction?: number
  daysToComplete?: number
  color: Hexcode
}

const ClassSessionInfo = ({
  sessionInfo,
  onSelectSession,
  isSelected,
}: {
  sessionInfo: SessionInfo
  onSelectSession: () => void
  isSelected: boolean
}) => {
  const { white } = useThemeContext()
  const { color, project, startDate, id, sessionIndex } = sessionInfo

  return (
    <AnimateInFlex
      col
      key={`info-${id}`}
      animation={ANIMATION_BY_KEY.leftSm}
      style={{
        userSelect: "none",
        cursor: "pointer",
        padding: "4px 8px",
        backgroundColor: color,
        border: `4px solid ${isSelected ? "black" : color}`,
        borderRadius: 4,
        color: white,
        boxSizing: "border-box",
        width: 220,
        height: 60,
        justifyContent: "space-between",
      }}
      onClick={onSelectSession}
    >
      <AnimateInFlex
        key={`start-date-${startDate}`}
        animation={ANIMATION_BY_KEY.leftLg}
      >
        {startDate}
      </AnimateInFlex>
      <AnimateInFlex
        key={`project-name-${startDate}-${sessionIndex}-${project?.id}-${project?.name}`}
        animation={ANIMATION_BY_KEY.leftLg}
      >
        <TextOverrun text={project?.name ?? "..."} />
      </AnimateInFlex>
    </AnimateInFlex>
  )
}

const ClassSessionOverview = ({
  curriculum,
  onSelectStudent,
  onSelectTeacher,
  onSelectAssistant,
}: {
  curriculum: CurriculumConstructor | undefined | null
  onSelectStudent: (studentId: Uuid) => void
  onSelectTeacher: (teacherId: Uuid) => void
  onSelectAssistant: (assistantId: Uuid) => void
}) => {
  const form = Form.useFormInstance()
  const { numberOfSessions = 0, projectIds = [] } = curriculum || {}
  const startDate = Form.useWatch<Dayjs>("startDate", form)
  const times = Form.useWatch<[Dayjs, Dayjs]>("times", form)
  const isSummerCamp = Form.useWatch<boolean>("isSummerCamp", form)
  const sessionIds = Form.useWatch<Uuid[]>("sessionIds", form)
  const name = Form.useWatch("name", form)

  // New classes don't have an id yet, but we just use this as a key
  const classId = Form.useWatch<Uuid>("id", form) ?? NULL_ID

  // Session selection state
  const [selectedSessionIndex, setSelectedSessionIndex] = useState<
    number | null
  >(0)
  const setSessionIndexRef = useRef<boolean>(false)

  const { modal } = App.useApp()

  // Get projects from db
  const stableProjectIds = useStableIds(projectIds)
  const projectsFromDb = useSomeDocs<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    stableProjectIds
  )

  const projectsFulfilled = useIsFulfilled(stableProjectIds, projectsFromDb)

  // Get sessions from db
  const stableSessionIds = useStableIds(sessionIds)
  const sessionsFromDb = useSomeDocs<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    stableSessionIds
  )

  // Auto-select closest session to today
  useEffect(() => {
    if (
      sessionsFromDb.length > 0 &&
      sessionIds?.length &&
      !setSessionIndexRef.current
    ) {
      let closestSessionIndex = 0

      // sessionsFromDb are already sorted
      for (let i = 0; i < sessionsFromDb.length; i++) {
        const session = sessionsFromDb[i]
        const eventStart = dayjs(session.eventStart)
        if (isTodayOrFuture(eventStart, getNow())) {
          closestSessionIndex = i
          break
        }
      }
      setSelectedSessionIndex(closestSessionIndex)
      setSessionIndexRef.current = true
    }
  }, [sessionIds, name, modal, sessionsFromDb, sessionsFromDb.length])

  // Get sessions data
  const sessionsInfo: SessionInfo[] = useMemo(() => {
    // projectsFromDb is unsorted
    const sortedProjects = projectsFulfilled
      ? (projectIds
          ?.map((id) => projectsFromDb.find((p) => p?.id === id))
          .filter(isDefined) ?? [])
      : []

    const projectSchedule = getProjectScheduleBySession(sortedProjects)

    const results: SessionInfo[] = []

    // Use numberOfSessions or sessionIds length, whichever is larger
    // If we have sessionIds, use that count; otherwise use numberOfSessions
    const sessionCount = Math.max(
      numberOfSessions,
      sessionIds?.length ?? 0,
      projectSchedule.length
    )

    const hasData = startDate && times && numberOfSessions != null
    const sessionDates = hasData
      ? generateSessionDates({
          startDate,
          times,
          isSummerCamp,
          numberOfSessions,
        })
      : []

    for (let i = 0; i < sessionCount; i++) {
      const schedule = projectSchedule[i] ?? {}
      const { fraction, daysToComplete, color, project } = schedule
      const isOverflow = i >= numberOfSessions

      const startDateLabel = sessionDates[i]?.eventStart
        ? dayjs(sessionDates[i].eventStart).format("M/D")
        : "..."

      const result: SessionInfo = {
        id: generateSessionId(classId, i),
        sessionIndex: i,
        startDate: startDateLabel,
        project,
        fraction,
        daysToComplete,
        color:
          project && color
            ? isOverflow
              ? `${color}60`
              : color
            : UNKNOWN_PROJECT_COLOR,
        isOverflow,
      }

      results.push(result)
    }

    return results
  }, [
    projectsFulfilled,
    projectsFromDb,
    projectIds,
    startDate,
    times,
    isSummerCamp,
    numberOfSessions,
    classId,
    sessionIds?.length,
  ])

  const selectedSessionInfo = sessionsInfo[selectedSessionIndex ?? 0]

  return (
    <Flex col gap={16}>
      <Flex gap={4} style={{ flexWrap: "wrap" }}>
        <AnimatePresence mode="popLayout">
          {sessionsInfo.map((sessionInfo, i) => (
            <ClassSessionInfo
              key={`session-info-${i}`}
              sessionInfo={sessionInfo}
              onSelectSession={() => setSelectedSessionIndex(i)}
              isSelected={i === selectedSessionIndex}
            />
          ))}
        </AnimatePresence>
      </Flex>
      {selectedSessionInfo && (
        <ClassSessionDetails
          sessionInfo={selectedSessionInfo}
          onSelectStudent={onSelectStudent}
          onSelectTeacher={onSelectTeacher}
          onSelectAssistant={onSelectAssistant}
        />
      )}
      {sessionsInfo.length > 0 && !selectedSessionInfo && (
        <Flex>No session selected</Flex>
      )}
      {sessionsInfo.length === 0 && !selectedSessionInfo && (
        <Flex>Please select a curriculum</Flex>
      )}
    </Flex>
  )
}

export default ClassSessionOverview
